import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import type {
  KitchenBoardPlate,
  KitchenBoardResponse,
} from "@/lib/api/types";
import { useErrorMessage } from "@/shared/errors";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useInvoiceCommands } from "@/shared/useInvoiceCommands";
import { usePrivateToast } from "@/shared/usePrivateToast";
import type { KitchenCommand } from "../components/PaperKitchen";
import { useStationPreference } from "./stationPreference";
import { useSession } from "@/modules/auth/useSession";
import { useTranslate } from "@/lib/i18n";
import { isOffPremise } from "./channel";

/**
 * How often the "waiting for" clocks re-render.
 *
 * Separate from server updates: the elapsed time is derived from
 * `firedAt`, which the server already sent, so ticking it needs no request. 15s
 * is under the 60s granularity the label is rendered at, so a minute never
 * visibly skips.
 */
const CLOCK_TICK_MS = 15_000;

/**
 * How long «تراجع» stays on the device that tapped (T3): ten seconds. The
 * server keeps the undo open for 15 minutes (the recall drawer's window).
 */
export const UNDO_TOAST_MS = 10_000;

/** The command that takes back a «جاهز» or a «تقديم»/«تسليم». */
export type KitchenUndo = "undoReady" | "undoServed";

/** Group plate ids by bill: one command per bill, as every board action. */
function byBill(targets: readonly KitchenBoardPlate[]): Map<string, string[]> {
  const bills = new Map<string, string[]>();
  for (const plate of targets) {
    bills.set(plate.invoiceId, [...(bills.get(plate.invoiceId) ?? []), plate.id]);
  }
  return bills;
}

/** `forget` for a station choice that was never stored. */
function noop() {}

/**
 * THE KITCHEN BOARD'S DATA AND COMMANDS — one board, two surfaces.
 *
 * `KitchenBoardPage` (the wall screen at `/kitchen`) and the PAPER floor's
 * Kitchen tab (2026-10-03) read the same board through this: the same query
 * key (so they share one cache and revision refresh), the same station filter (the
 * wall screen's remembered per device, the tab's for as long as it is open —
 * `remember`), and the same commands through the idempotent envelope. Moved here verbatim
 * from the page; the page keeps its own frame (header, lanes height, the
 * per-plate board of the other looks).
 */
export function useKitchenBoard({
  enabled,
  remember = true,
  frozen = false,
  everyStation = false,
}: {
  /** `RESTAURANT_KITCHEN_VIEW` — the board endpoint's own gate. */
  enabled: boolean;
  /**
   * Whether the station choice is this DEVICE's (the wall screen's remembered
   * station) or only this surface's. The floor's Kitchen tab passes `false`:
   * it opens on every station — the whole kitchen at a glance — and a filter
   * picked there lasts while the tab is open, without re-pointing the device's
   * `/kitchen` wall screen.
   */
  remember?: boolean;
  /**
   * The wall board's live updates are down (T12, F4): the clocks stop at the
   * last time the board was actually read, and the 15 s tick is suspended.
   * Under the disconnected band a clock that kept counting would claim a
   * freshness the board no longer has. The tick is a re-render, not a fetch,
   * so freezing it changes no request either way.
   */
  frozen?: boolean;
  /**
   * The order view (D170) reads every station within the session's D128
   * scope, whatever station the device remembers: the board asks for "all"
   * while it is set, and the remembered station is kept for the way back.
   */
  everyStation?: boolean;
}) {
  const { dispatchCommand } = useInvoiceCommands();
  const t = useTranslate();
  const toast = usePrivateToast();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();

  const preference = useStationPreference();
  const [localStationId, setLocalStationId] = useState<string | null>(null);
  const chosenStationId = remember ? preference.stationId : localStationId;
  /*
   * D128 — the member's role may keep them to some stations. The server
   * already answers "all" with only those and refuses the rest with a 403, so
   * the board must never ASK for another: a device remembering the bar for a
   * grill cook would otherwise sit on an error. With exactly one station there
   * is nothing to choose — the board opens on it and "All stations" goes.
   */
  const { data: session } = useSession();
  const lockedStationIds = session?.seller.kitchenStationIds ?? null;
  const pinnedStationId =
    lockedStationIds?.length === 1 ? lockedStationIds[0] : null;
  const stationId =
    pinnedStationId ??
    (chosenStationId !== null &&
    lockedStationIds !== null &&
    !lockedStationIds.includes(chosenStationId)
      ? null
      : chosenStationId);
  /** Whether the chip row offers "All stations" at all. */
  const canShowAllStations = pinnedStationId === null;
  const setStationId = remember ? preference.setStationId : setLocalStationId;
  // A local choice has nothing stored to forget.
  const forget = remember ? preference.forget : noop;
  /**
   * The remembered station no longer exists.
   *
   * A device keeps its station id in local storage, and a station can be
   * deleted, or the tablet can be moved to another restaurant on the same
   * account. The board then asked for plates at an id nobody knows: the
   * endpoint answers with zero plates and the chip row filters the dead chip
   * out, so the screen said "the kitchen is empty" over a kitchen that was not.
   * The preference is dropped and the board falls back to every station, with
   * one line saying so — silently widening the filter would be its own lie.
   */
  const [stationGone, setStationGone] = useState(false);
  /**
   * Plates with a command in flight.
   *
   * A set rather than a single id: a cook bumping four plates off one ticket
   * taps four times in about a second, and a single-flight lock would swallow
   * three of them. Each entry disables only its own card's buttons.
   */
  const [busyItemIds, setBusyItemIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /**
   * The board carries its own station list.
   *
   * A cook holds `RESTAURANT_KITCHEN_VIEW` and nothing else — deliberately not
   * `RESTAURANT_MENU_VIEW`, which also reads prices and store settings, and is
   * the opposite of why a chit carries no prices. So the stations endpoint is
   * closed to them, and the board endpoint returns the list alongside the
   * plates rather than making the screen ask twice with two permissions.
   */
  const boardStationId = everyStation ? null : stationId;
  const board = useApiQuery<KitchenBoardResponse>(
    queryKeys.kitchenBoard(boardStationId),
    endpoints.restaurant.kitchenBoard(boardStationId ?? undefined),
    {
      enabled,
      // The wall screen is never focused, so this earns nothing here — but a
      // cook checking the board from a phone in their pocket is the same case
      // the floor grid handles, and it costs one request.
      refetchOnWindowFocus: true,
    },
  );

  // A ticking "now" so the waiting clocks advance between server updates. Derived, not
  // fetched: see CLOCK_TICK_MS. Frozen (P5/5.4), it is pinned to the last read
  // and no interval exists; live again, it is at least that read's time at once.
  const [ticked, setTicked] = useState(() => Date.now());
  useEffect(() => {
    if (frozen) return;
    const timer = window.setInterval(() => setTicked(Date.now()), CLOCK_TICK_MS);
    return () => window.clearInterval(timer);
  }, [frozen]);
  const lastRead = board.dataUpdatedAt;
  const now = frozen && lastRead > 0 ? lastRead : Math.max(ticked, lastRead);

  const plates = useMemo(() => board.data?.plates ?? [], [board.data]);
  const stationList = useMemo(() => board.data?.stations ?? [], [board.data]);

  /*
   * Validate the restored preference against the list the board just returned.
   *
   * Only once data has ARRIVED — checking against an empty list mid-load would
   * throw away a perfectly good station on every refresh — and the list is the
   * whole station set (active and paused alike), so a paused station is kept.
   *
   * This is a legitimate effect and not a derived value: the authority on
   * whether the station still exists is an EXTERNAL system (the server's
   * station list), the remembered id lives in a SECOND external system (local
   * storage) that has to be corrected, and the fix changes the query key so
   * the board refetches unfiltered. It runs at most once per dead id: the
   * first pass clears the preference, and `stationId` is then null.
   */
  useEffect(() => {
    if (!board.data || stationId === null) return;
    if (stationList.some((station) => station.id === stationId)) return;
    forget();
    setStationId(null);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the notice must OUTLIVE the state it reports on: clearing the preference above makes "the saved station is missing" false again, so a derived flag would vanish on the very next paint and the cook would see a silently widened board with no explanation.
    setStationGone(true);
  }, [board.data, forget, stationId, stationList, setStationId]);

  /**
   * Sends one transition and refreshes the board.
   *
   * Everything goes through `dispatchCommand` — the board never builds its own
   * fetch. That is what keeps it inside the idempotency envelope: a cook double
   * tapping "start" on a busy pass is a routine event, and outside the envelope
   * it would be a second COOKED depletion against the same plate.
   */
  const run = useCallback(
    async (
      plate: KitchenBoardPlate,
      type: "startPreparing" | "markReady" | "markServed",
    ) => {
      setBusyItemIds((current) => new Set(current).add(plate.id));
      try {
        await dispatchCommand({
          type,
          invoiceId: plate.invoiceId,
          payload: { itemIds: [plate.id] },
        });
        await invalidateInvoiceMutation(queryClient, plate.invoiceId);
      } catch (failure) {
        toast.error(errorMessage(failure));
      } finally {
        setBusyItemIds((current) => {
          const next = new Set(current);
          next.delete(plate.id);
          return next;
        });
      }
    },
    [dispatchCommand, errorMessage, queryClient, toast],
  );

  /**
   * `run` for a whole card: the PAPER board's ticket and line buttons act on
   * several plates at once. One command per BILL (a ticket never spans two,
   * but the grouping is not trusted to guarantee it), each carrying every
   * plate id — the same envelope and the same idempotency as a single plate.
   * Resolves `true` once every command landed and `false` when one failed
   * (already toasted) — the served card's "Well done!" stands down on false.
   */
  const runMany = useCallback(
    async (targets: KitchenBoardPlate[], type: KitchenCommand): Promise<boolean> => {
      const ids = targets.map((plate) => plate.id);
      setBusyItemIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.add(id));
        return next;
      });
      try {
        const byInvoice = new Map<string, string[]>();
        for (const plate of targets) {
          byInvoice.set(plate.invoiceId, [
            ...(byInvoice.get(plate.invoiceId) ?? []),
            plate.id,
          ]);
        }
        for (const [invoiceId, itemIds] of byInvoice) {
          await dispatchCommand({ type, invoiceId, payload: { itemIds } });
          await invalidateInvoiceMutation(queryClient, invoiceId);
        }
        return true;
      } catch (failure) {
        toast.error(errorMessage(failure));
        return false;
      } finally {
        setBusyItemIds((current) => {
          const next = new Set(current);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [dispatchCommand, errorMessage, queryClient, toast],
  );

  /**
   * Plates whose undo is in flight. A ref, not state: a double tap on «تراجع»
   * lands twice in one frame, before any re-render, and the second would send
   * a fresh command that the server refuses (the first already moved the
   * plate back) — a 409 toast right after the success.
   */
  const undoing = useRef<Set<string>>(new Set());

  /**
   * TAKE A TAP BACK (T3, D167) — `undoReady` / `undoServed` through the same
   * envelope, with the SAME plate ids, one command per bill. The server is the
   * judge (bill still OPEN, inside 15 minutes, a person's serve): a refusal is
   * a toast like any other, never a silent no-op. A second tap while the first
   * is in flight sends nothing.
   */
  const undo = useCallback(
    async (targets: readonly KitchenBoardPlate[], type: KitchenUndo): Promise<boolean> => {
      const ids = targets.map((plate) => plate.id);
      if (ids.some((id) => undoing.current.has(id))) return false;
      ids.forEach((id) => undoing.current.add(id));
      try {
        for (const [invoiceId, itemIds] of byBill(targets)) {
          await dispatchCommand({ type, invoiceId, payload: { itemIds } });
          await invalidateInvoiceMutation(queryClient, invoiceId);
        }
        return true;
      } catch (failure) {
        toast.error(errorMessage(failure));
        return false;
      } finally {
        ids.forEach((id) => undoing.current.delete(id));
      }
    },
    [dispatchCommand, errorMessage, queryClient, toast],
  );

  /**
   * `runMany`, then — for «جاهز» and «تقديم»/«تسليم» — the ten-second toast
   * «{state} · #N» with «تراجع» on THIS device (T3). The card itself leaves
   * when the server answers and the board re-reads; there is no hold.
   */
  const runWithUndo = useCallback(
    async (targets: KitchenBoardPlate[], type: KitchenCommand): Promise<boolean> => {
      const ok = await runMany(targets, type);
      if (!ok || type === "startPreparing" || targets.length === 0) return ok;
      const state =
        type === "markReady"
          ? t((m) => m.kitchen.laneReady)
          : targets.some((plate) => isOffPremise(plate.channel))
            ? t((m) => m.kitchen.stateHandedOver)
            : t((m) => m.kitchen.stateServed);
      const number = [...new Set(targets.map((plate) => plate.dailySequence))].join(", #");
      const taken = [...targets];
      toast.success(t((m) => m.kitchen.undoToast, { state, number }), undefined, {
        durationMs: UNDO_TOAST_MS,
        action: {
          label: t((m) => m.kitchen.undo),
          onClick: () => void undo(taken, type === "markReady" ? "undoReady" : "undoServed"),
        },
      });
      return ok;
    },
    [runMany, t, toast, undo],
  );

  return {
    board,
    plates,
    stationList,
    stationId,
    boardStationId,
    setStationId,
    canShowAllStations,
    stationGone,
    setStationGone,
    busyItemIds,
    now,
    run,
    runMany,
    runWithUndo,
    undo,
  };
}
